import React, { useState } from 'react';
import { register } from '../../services/auth';
import { AnalyticsEvents } from '../../services/analytics';
import FormField from '../FormField';
import Input from '../Input';
import LottieAnimation from './LottieAnimation';
import ProxyImage from '../ProxyImage';
import './Auth.css';
interface RegisterProps {
  onSuccess: () => void;
  onSwitchToLogin: () => void;
  onVerifyCode?: (email: string) => void;
}
const Register: React.FC<RegisterProps> = ({ onSuccess, onSwitchToLogin, onVerifyCode }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setMessage(null);
    // Validación
    const newErrors: Record<string, string> = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.password) {
      newErrors.password = 'Contraseña requerida';
    } else if (formData.password.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      const result = await register(formData);
      if (result.success) {
        // Track registro exitoso
        AnalyticsEvents.userRegister('email');
        setMessage('Registro exitoso. Se ha enviado un código de verificación a tu email.');
        // Si hay callback para verificar código, usarlo; sino, volver al login después de 2 segundos
        if (onVerifyCode) {
          setTimeout(() => {
            onVerifyCode(formData.email);
          }, 1500);
        } else {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } else {
        const errorMessage = result.error || 'Error al registrar';
        const canRetry = (result as any).canRetry === true;
        const debugInfo = (result as any).debug;
        // Si el usuario fue creado pero falló el envío del correo, redirigir a verificación
        if (canRetry) {
          setErrors({ 
            general: errorMessage || 'El usuario fue creado pero no se pudo enviar el correo de verificación. Puedes solicitar un nuevo código en la pantalla de verificación.'
          });
          // Redirigir automáticamente a la pantalla de verificación
          if (onVerifyCode) {
            setTimeout(() => {
              onVerifyCode(formData.email);
            }, 2000);
          }
        } else {
        setErrors({ general: errorMessage });
        }
      }
    } catch (error) {
      setErrors({ general: 'Error de conexión. Por favor, intenta nuevamente.' });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        <div className="auth-lottie-column">
          <div className="auth-logo">
            <a 
              href="https://smartcityexposantiagodelestero.com/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <ProxyImage 
                src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png" 
                alt="Smart City Expo Santiago del Estero" 
              />
            </a>
          </div>
          <LottieAnimation />
          <div className="auth-partners-image">
            <ProxyImage 
              src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Instituciones-Colaboradoras-Web-scaled-e1767776957958.png" 
              alt="Instituciones Colaboradoras" 
            />
          </div>
        </div>
        <div className="auth-form-column">
          <div className="auth-card">
            <div className="auth-logo-mobile">
              <a 
                href="https://smartcityexposantiagodelestero.com/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <ProxyImage 
                  src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png" 
                  alt="Smart City Expo Santiago del Estero" 
                />
              </a>
            </div>
        <h2 className="auth-title">Crear Cuenta</h2>
        <p className="auth-subtitle">Regístrate para gestionar tu perfil</p>
        {message && (
          <div className="auth-message success">
            {message}
          </div>
        )}
        {errors.general && (
          <div className="auth-message error">
            {errors.general}
          </div>
        )}
        <form onSubmit={handleSubmit} className="auth-form">
          <FormField label="Correo electrónico" required error={errors.email}>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="correo@ejemplo.com"
              error={!!errors.email}
              disabled={loading}
            />
          </FormField>
          <FormField label="Contraseña" required error={errors.password}>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Mínimo 8 caracteres"
              error={!!errors.password}
              disabled={loading}
            />
          </FormField>
          <FormField label="Confirmar contraseña" required error={errors.confirmPassword}>
            <Input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Repite tu contraseña"
              error={!!errors.confirmPassword}
              disabled={loading}
            />
          </FormField>
          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'REGISTRANDO...' : 'REGISTRARSE'}
          </button>
        </form>
        <div className="auth-switch">
          <p>¿Ya tienes cuenta? <button type="button" onClick={onSwitchToLogin} className="auth-link">Inicia sesión</button></p>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Register;
